/**
 * Search utility functions for recipe filtering
 */

/**
 * Search recipes by title with case-insensitive matching
 * @param {Array} recipes - Array of recipe objects
 * @param {string} searchQuery - Search query string
 * @returns {Array} Filtered array of recipes
 */
export const searchRecipesByTitle = (recipes, searchQuery) => {
  if (!searchQuery || !searchQuery.trim()) {
    return recipes;
  }
  
  const query = searchQuery.toLowerCase().trim();
  
  return recipes.filter(recipe => 
    recipe.title && recipe.title.toLowerCase().includes(query)
  );
};

/**
 * Search recipes by title and tags with case-insensitive matching
 * @param {Array} recipes - Array of recipe objects
 * @param {string} searchQuery - Search query string
 * @param {Object} recipeTags - Object mapping recipe IDs to arrays of tag objects
 * @returns {Array} Filtered array of recipes
 */
export const searchRecipesByTitleAndTags = (recipes, searchQuery, recipeTags = {}) => {
  if (!searchQuery || !searchQuery.trim()) {
    return recipes;
  }
  
  const query = searchQuery.toLowerCase().trim();
  
  return recipes.filter(recipe => {
    // Search in recipe title
    const titleMatch = recipe.title && recipe.title.toLowerCase().includes(query);
    
    // Search in recipe tags
    const tags = recipeTags[recipe.id] || [];
    const tagMatch = tags.some(tag => 
      tag.name && tag.name.toLowerCase().includes(query)
    );
    
    return titleMatch || tagMatch;
  });
};

/**
 * Get search match information for a recipe
 * @param {Object} recipe - Recipe object
 * @param {string} searchQuery - Search query string
 * @param {Array} tags - Array of tag objects for this recipe
 * @returns {Object} Match information
 */
export const getSearchMatchInfo = (recipe, searchQuery, tags = []) => {
  if (!searchQuery || !searchQuery.trim()) {
    return { titleMatch: false, tagMatches: [] };
  }
  
  const query = searchQuery.toLowerCase().trim();
  
  const titleMatch = recipe.title && recipe.title.toLowerCase().includes(query);
  const tagMatches = tags.filter(tag => 
    tag.name && tag.name.toLowerCase().includes(query)
  );
  
  return { titleMatch, tagMatches };
};

/**
 * Highlight matching text in recipe titles for display
 * @param {string} text - Original text
 * @param {string} searchQuery - Search query to highlight
 * @returns {string} Text with highlighted matches
 */
export const highlightSearchText = (text, searchQuery) => {
  if (!searchQuery || !searchQuery.trim()) {
    return text;
  }
  
  const regex = new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  return text.replace(regex, '<mark>$1</mark>');
};

/**
 * Get search results summary text
 * @param {number} resultCount - Number of search results
 * @param {string} searchQuery - Search query
 * @returns {string} Summary text
 */
export const getSearchSummary = (resultCount, searchQuery) => {
  if (!searchQuery || !searchQuery.trim()) {
    return '';
  }
  
  const recipeText = resultCount === 1 ? 'recipe' : 'recipes';
  return `Found ${resultCount} ${recipeText} matching "${searchQuery}"`;
};

/**
 * Get enhanced search results summary with match type information
 * @param {Array} recipes - Filtered recipes array
 * @param {string} searchQuery - Search query
 * @param {Object} recipeTags - Object mapping recipe IDs to arrays of tag objects
 * @returns {string} Enhanced summary text
 */
export const getEnhancedSearchSummary = (recipes, searchQuery, recipeTags = {}) => {
  if (!searchQuery || !searchQuery.trim()) {
    return '';
  }
  
  const recipeText = recipes.length === 1 ? 'recipe' : 'recipes';
  
  // Count matches by type
  let titleMatches = 0;
  let tagMatches = 0;
  
  recipes.forEach(recipe => {
    const matchInfo = getSearchMatchInfo(recipe, searchQuery, recipeTags[recipe.id] || []);
    if (matchInfo.titleMatch) titleMatches++;
    if (matchInfo.tagMatches.length > 0) tagMatches++;
  });
  
  let summary = `Found ${recipes.length} ${recipeText} matching "${searchQuery}"`;
  
  if (titleMatches > 0 && tagMatches > 0) {
    summary += ` (${titleMatches} by title, ${tagMatches} by tags)`;
  } else if (titleMatches > 0) {
    summary += ` (by title)`;
  } else if (tagMatches > 0) {
    summary += ` (by tags)`;
  }
  
  return summary;
};
